import React from 'react'
import noop from 'lodash/noop'
import { Text, Flex, Box, UnstyledButton, SystemProps } from '@chilipiper/design-system'
import { SvgStar } from '@chilipiper/icons/src/design-system/Star'
import { Session, SessionType } from '@chilipiper/models'
import { Icon, ErrorAlert, SuccessAlert } from '@chilipiper/design-system'
import { mixpanelSdk as mixpanel } from '@chilipiper/service/src/mixpanel'
import { UserAvatar } from '../../../avatar'
import { BookingLinksCardSelect } from '../cards/BookingLinksCardSelect'
import { copyBookingLink } from '../cards/helpers'
import { useGetTemplate } from '../cards/hooks'
import { CopyLinkType } from '../cards/types'
import { getLinkForCurrentUser } from '../../helpers'
import { ApiBookingLink } from '../../types'
import { StyledContainer, StyledCopyLink } from './styles'
import { getSingleUseLinkData } from './helpers'

const MAX_MEMBERS_SHOWN = 3

interface Props extends SystemProps {
  bookingLink: ApiBookingLink
  favourite?: boolean
  footer?: JSX.Element
  onCopyLinkClicked?: (alert: JSX.Element, link?: string) => void
  session?: SessionType
  toogleFavourite?: () => void
}

export const BookingLink = ({
  footer,
  bookingLink,
  favourite,
  onCopyLinkClicked,
  session = Session,
  toogleFavourite = noop,
  ...others
}: Props) => {
  const membersCount = bookingLink?.members?.length
  const link = getLinkForCurrentUser(bookingLink, session) || ''
  const onCopyLinkClick = (copyLinkType: CopyLinkType, link?: string) => {
    const { message, success } = copyBookingLink(copyLinkType, link)
    const Alert = success ? SuccessAlert : ErrorAlert
    onCopyLinkClicked?.(<Alert message={message} />, link)
    mixpanel.track('Copy Booking Link', {
      source: 'Chrome Extension',
      componentSource: 'booking-links',
      linkWorkspaceType: bookingLink.isPersonal ? 'Personal' : 'Team',
      isFavorite: favourite,
      duration: bookingLink.duration,
    })
  }
  const linkName = session.user().linkName
  const template = useGetTemplate(bookingLink.templateId)

  return template?.active ? (
    <StyledContainer bg='white'>
      <Flex flexDirection='row' {...others}>
        <Flex flexDirection='column' pb={!footer ? 4 : null} pr={4}>
          <Flex mb={2} alignItems='center'>
            <UnstyledButton w='auto' pl={4} pr={2} pt={4} onClick={toogleFavourite}>
              <Icon
                pointer
                w={3}
                icon={SvgStar}
                color={favourite ? 'yellow-light-100' : 'neutral-light-100'}
              />
            </UnstyledButton>
            <Text pt={4}>{bookingLink.name}</Text>
          </Flex>
          <Flex pl={4} alignItems='center'>
            {bookingLink?.members?.slice(0, MAX_MEMBERS_SHOWN).map(member => (
              <Box mr={2} key={member.id}>
                <UserAvatar key={member.id} id={member.id} name={member.name} borderRadius={8} />
              </Box>
            ))}
            {membersCount > MAX_MEMBERS_SHOWN && (
              <Flex
                px={1}
                h={6}
                mr={3}
                alignItems='center'
                justifyContent='center'
                bg='neutral-dark-8'
                borderRadius={8}
              >
                <Text color='neutral-dark-80' fontSize={12}>
                  +{membersCount - MAX_MEMBERS_SHOWN}
                </Text>
              </Flex>
            )}
            <Text color='neutral-dark-80' fontSize={12}>
              {bookingLink.duration} minutes
            </Text>
          </Flex>
        </Flex>
        {template?.singleUseLink ? (
          <BookingLinksCardSelect
            p={0}
            ml='auto'
            mr={2}
            w='auto'
            popoverText='Copy Link'
            onSelectCopy={onCopyLinkClick}
            bookingLinkUrl={link}
            {...getSingleUseLinkData({
              bookingLink,
              linkName,
              isGroupType: membersCount > 0,
            })}
          />
        ) : (
          <UnstyledButton
            minWidth={92}
            ml='auto'
            w='auto'
            px={4}
            onClick={() => onCopyLinkClick(CopyLinkType.CopyLink, link)}
          >
            <StyledCopyLink>Copy Link</StyledCopyLink>
          </UnstyledButton>
        )}
      </Flex>
      {footer}
    </StyledContainer>
  ) : null
}
